import React, { useEffect, useMemo, useRef, useState } from "react";
import { UploadFile } from "@/api/integrations";
import { ArrowLeft, Upload, File, Copy, Check, ExternalLink, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { motion } from "framer-motion";

const LS_KEY = "RB_FILES";

export default function FileUploadPage() {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const inputRef = useRef(null);

  // --- utils ---
  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };
  const shortUrl = (u) => (!u ? "" : u.length > 90 ? `${u.slice(0, 48)}…${u.slice(-24)}` : u);

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1200);
  };

  // --- persistence ---
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
      setUploadedFiles(saved);
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(uploadedFiles));
  }, [uploadedFiles]);

  // --- upload handlers ---
  const handleFileUpload = async (files) => {
    setIsUploading(true);
    const toAdd = [];

    for (const file of files) {
      try {
        // Il wrapper normalizza: può tornare { file_url } o { url }
        const res = await UploadFile(file); // passiamo direttamente il File/Blob
        const url = res?.file_url || res?.url || "";
        toAdd.push({
          id: crypto.randomUUID(),
          name: file.name,
          url,
          size: file.size,
          type: res?.mime_type || file.type || "application/octet-stream",
        });
      } catch (error) {
        console.error("Errore upload file:", error);
      }
    }

    setUploadedFiles((prev) => [...toAdd, ...prev]);
    setIsUploading(false);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) handleFileUpload(files);
    // reset per poter ricaricare lo stesso file due volte
    e.target.value = "";
  };

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragIn = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragOut = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) handleFileUpload(files);
  };

  const removeOne = (id) => {
    setUploadedFiles((prev) => prev.filter((x) => x.id !== id));
  };

  const empty = useMemo(() => !uploadedFiles || uploadedFiles.length === 0, [uploadedFiles]);

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <Button variant="outline" onClick={() => navigate(createPageUrl("Dashboard"))} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Bacheca
          </Button>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Carica <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">File Audio</span>
          </h1>
          <p className="text-lg text-gray-600">
            Carica i tuoi file audio per le lezioni e ottieni gli URL da utilizzare nell'app.
          </p>
        </motion.div>

        {/* Upload Area */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-6 h-6" />
                Carica File Audio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
                }`}
                onDragEnter={handleDragIn}
                onDragLeave={handleDragOut}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                role="button"
                onClick={() => inputRef.current?.click()}
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-600 mb-2">
                  Trascina i file qui o clicca per selezionare
                </p>
                <p className="text-sm text-gray-500 mb-4">Supportati: MP3, WAV, OGG, M4A (Max 50MB per file)</p>

                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept="audio/*"
                  onChange={handleFileInput}
                  className="hidden"
                />

                <Button disabled={isUploading} className="bg-gradient-to-r from-blue-500 to-purple-600">
                  {isUploading ? "Caricamento..." : "Seleziona File"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Uploaded Files List */}
        {!empty && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <File className="w-6 h-6" />
                  File Caricati ({uploadedFiles.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className="flex items-start justify-between p-4 border rounded-lg">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                          <File className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{file.name}</p>
                          <p className="text-sm text-gray-500">{formatFileSize(file.size)} · {file.type || "audio"}</p>

                          {/* URL compatto + details per full URL */}
                          <div className="mt-2 text-xs text-gray-500 break-all">
                            <span className="font-semibold">URL: </span>
                            <span title={file.url}>{shortUrl(file.url)}</span>
                          </div>
                          <details className="mt-2">
                            <summary className="text-xs text-gray-500 cursor-pointer">Mostra URL completo</summary>
                            <textarea
                              readOnly
                              className="mt-2 w-full h-28 text-xs font-mono bg-slate-50 p-2 rounded-lg border"
                              value={file.url}
                              onFocus={(e) => e.target.select()}
                            />
                          </details>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm inline-flex items-center gap-1"
                          title="Apri in nuova scheda"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Apri
                        </a>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(file.url, file.id)}
                          className="flex items-center gap-1"
                          title="Copia URL negli appunti"
                        >
                          {copiedId === file.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          {copiedId === file.id ? "Copiato" : "Copia URL"}
                        </Button>
                        <audio controls className="w-56">
                          <source src={file.url} type={file.type} />
                        </audio>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-600 hover:text-rose-700"
                          onClick={() => removeOne(file.id)}
                          title="Rimuovi"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-900">Come usare questi URL</h4>
                  <p className="text-sm text-green-700 mt-1">
                    1) Clicca <strong>Copia URL</strong> sul file desiderato.<br />
                    2) Vai in <strong>Dashboard → Data → Lesson</strong>.<br />
                    3) Incolla l’URL nel campo <code>audio_url</code> e salva.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
